import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Scale } from "lucide-react";
import AppFooter from "@/components/layout/AppFooter";

/**
 * Terms & Conditions page.
 *
 * Plain-English headers per product spec, formal legal body copy.
 * Publicly accessible (not behind auth) so the DisclaimerDialog and
 * RegisterPage can link to it.
 */
const Section = ({ n, title, children }) => (
  <section className="space-y-3" data-testid={`terms-section-${n}`}>
    <h2 className="text-lg font-semibold tracking-tight">
      {n}. {title}
    </h2>
    <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
  </section>
);

const SubSection = ({ id, title, children }) => (
  <div className="space-y-1.5" data-testid={`terms-subsection-${id}`}>
    <h3 className="text-sm font-semibold text-foreground">{id} {title}</h3>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="terms-page">
      <header className="border-b">
        <div className="mx-auto max-w-3xl px-5 py-4 flex items-center justify-between">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <div className="inline-flex items-center gap-2 text-sm font-semibold">
            <Scale className="h-4 w-4 text-primary" /> CSE Investor
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-3xl px-5 py-10">
        <div className="space-y-1.5 mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Terms &amp; Conditions</h1>
          <p className="text-sm text-muted-foreground">
            Version 1.0 &middot; Last updated 7 May 2026
          </p>
        </div>

        <Card>
          <CardContent className="p-6 md:p-8 space-y-8">
            <Section n={1} title="Acceptance of Terms">
              <p>
                By creating an account or otherwise accessing this platform ("CSE Investor",
                "we", "us") you agree to be bound by these Terms &amp; Conditions. If you do
                not agree you must not use this platform.
              </p>
            </Section>

            <Section n={2} title="Not Financial Advice">
              <p>
                The content, tools, scores, analyses and recommendations presented on this
                platform are for <strong>informational and research purposes only</strong>.
                Nothing on this platform constitutes financial, investment, tax, accounting
                or legal advice. We are not a licensed stockbroker, investment adviser,
                wealth manager or financial planner.
              </p>
              <p>
                You should consult a qualified and licensed professional before making any
                investment decision.
              </p>
            </Section>

            <Section n={3} title="Your Responsibility">
              <p>
                You alone are responsible for every investment decision you make using this
                platform. You accept full responsibility for any gains, losses, taxes or
                other consequences arising from those decisions.
              </p>
            </Section>

            <Section n={4} title="Investment Risk">
              <p>
                Investing in stocks, ETFs, bonds, derivatives or any other security carries
                risk, including the possible loss of your entire principal. Past performance
                is not indicative of future results. Prices can fall as well as rise.
              </p>
            </Section>

            <Section n={5} title="AI and Algorithmic Output">
              <p>
                Portions of this platform use AI models (large language models, computer
                vision, statistical scoring) to generate summaries, extract data, and
                produce recommendations. AI output may be incomplete, outdated or simply
                wrong. You must treat it as an input to your own research, not as a
                conclusion to be acted on.
              </p>
            </Section>

            <Section n={6} title="No Financial Liability">
              <p>
                To the fullest extent permitted by applicable law, CSE Investor, its
                operators, affiliates, employees and third-party data providers shall not
                be liable for any direct, indirect, incidental, consequential, special or
                punitive damages arising from your use of, or inability to use, this
                platform, including without limitation lost profits, lost trading
                opportunities, or losses resulting from reliance on any content displayed
                here.
              </p>
              <p>
                You agree to indemnify and hold harmless CSE Investor against any claim
                brought by any third party arising out of your use of the platform or
                your breach of these Terms.
              </p>
            </Section>

            <Section n={7} title="Data Accuracy and Reliability">
              <SubSection id="7.1" title="Where We Get Our Data">
                <p>This platform sources financial data from third-party providers including but not limited to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Yahoo Finance / yfinance library</li>
                  <li>SEC EDGAR (US filings)</li>
                  <li>Colombo Stock Exchange (CSE) official publications and PDFs</li>
                  <li>TradingView technical signals</li>
                  <li>Company annual reports</li>
                </ul>
                <p>
                  We do not control these data sources and cannot guarantee their accuracy,
                  completeness or timeliness.
                </p>
              </SubSection>

              <SubSection id="7.2" title="Data May Contain Errors">
                <p>Financial fundamentals shown on this platform including but not limited to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Revenue and earnings figures</li>
                  <li>PE ratios and valuation multiples</li>
                  <li>Dividend yields and amounts</li>
                  <li>EPS and book value per share</li>
                  <li>Debt and cash positions</li>
                  <li>Analyst price targets</li>
                  <li>Historical price data</li>
                </ul>
                <p>may contain errors, delays, variances or inaccuracies.</p>
                <p>
                  Some data is extracted from scanned PDF documents using AI Vision
                  technology which may introduce additional errors.
                </p>
              </SubSection>

              <SubSection id="7.3" title="Data May Be Delayed">
                <p>Data shown may be delayed or outdated due to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Data provider update schedules</li>
                  <li>Quarterly vs annual reporting</li>
                  <li>CSE publication delays</li>
                  <li>System processing delays</li>
                  <li>API rate limits and failures</li>
                </ul>
                <p>Real-time or intraday data is not provided.</p>
              </SubSection>

              <SubSection id="7.4" title="Differences Between Platforms">
                <p>There may be variances between:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Data shown on this platform</li>
                  <li>Data from your broker</li>
                  <li>Data from official CSE sources</li>
                  <li>Data from other financial platforms</li>
                </ul>
                <p>
                  These variances are normal and expected. Always verify critical figures
                  from official primary sources:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>CSE official website (cse.lk)</li>
                  <li>Company annual reports</li>
                  <li>SEC EDGAR for US stocks</li>
                  <li>Your licensed stockbroker</li>
                </ul>
              </SubSection>

              <SubSection id="7.5" title="AI Document Extraction">
                <p>
                  For CSE stocks some financial data is extracted from annual reports using
                  AI Vision technology. This process may introduce errors especially for:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Scanned or low quality documents</li>
                  <li>Complex table structures</li>
                  <li>Multi-currency statements</li>
                  <li>Restated figures</li>
                </ul>
                <p>Always verify extracted figures against the original annual report.</p>
              </SubSection>

              <SubSection id="7.6" title="No Data Guarantee">
                <p>
                  We make no warranty express or implied regarding the accuracy,
                  completeness, currency or reliability of any data shown on this platform.
                  You use all data entirely at your own risk.
                </p>
              </SubSection>
            </Section>

            <Section n={8} title="Account and Security">
              <p>
                You are responsible for keeping your login credentials secure and for any
                activity that occurs under your account. Notify us immediately if you
                suspect unauthorised access.
              </p>
            </Section>

            <Section n={9} title="Changes to These Terms">
              <p>
                We may revise these Terms from time to time. When we make a material change
                we will prompt you to re-accept the updated version on your next login.
              </p>
            </Section>

            <Section n={10} title="Governing Law">
              <p>
                These Terms are governed by the laws of Sri Lanka. Any dispute arising
                from your use of the platform is subject to the exclusive jurisdiction of
                the courts of Sri Lanka.
              </p>
            </Section>
          </CardContent>
        </Card>

        <div className="mt-8 flex justify-center">
          <Link to="/login">
            <Button variant="outline" data-testid="terms-back-to-login">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Sign in
            </Button>
          </Link>
        </div>
      </main>

      <AppFooter />
    </div>
  );
};

export default TermsPage;
